import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { formatDateTime, formatMoney } from './billingShared';
import { studentLabel } from '../utils/studentLabel';

function uniqueAccountTotals(rows) {
  const seen = new Set();
  let prepaid = 0;
  let debt = 0;
  for (const row of rows) {
    const key = row.account_id ?? `student-${row.student_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    prepaid += Number(row.prepaid) || 0;
    debt += Number(row.debt) || 0;
  }
  return { prepaid, debt, balance: prepaid - debt };
}

function mergeBillingRows(rows, layoutItems) {
  const byId = new Map(rows.map((row) => [row.student_id, row]));
  const merged = [];
  const seen = new Set();
  for (const item of layoutItems) {
    if (item.type === 'divider') {
      merged.push({
        kind: 'divider',
        key: item.id != null ? `d-${item.id}` : `d-tmp-${merged.length}`,
        title: item.title || '',
      });
    } else if (item.type === 'student' && byId.has(item.id)) {
      merged.push({ kind: 'student', key: `s-${item.id}`, row: byId.get(item.id) });
      seen.add(item.id);
    }
  }
  for (const row of rows) {
    if (!seen.has(row.student_id)) {
      merged.push({ kind: 'student', key: `s-${row.student_id}`, row });
    }
  }
  return merged;
}

function todayMoscowDate() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function summarizeTodayReports(reports) {
  const active = (reports || []).filter((r) => r.status !== 'cancelled');
  let total = 0;
  let charged = 0;
  let pending = 0;
  for (const r of active) {
    const amount = Number(r.amount) || 0;
    total += amount;
    if (r.status === 'charged') charged += amount;
    else if (r.status === 'awaiting_student') pending += amount;
  }
  return { reports: active, total, charged, pending };
}

export default function TeacherBillingPage() {
  const [rows, setRows] = useState([]);
  const [layoutItems, setLayoutItems] = useState([]);
  const [todayReports, setTodayReports] = useState([]);
  const [periodEarnings, setPeriodEarnings] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncingExt, setSyncingExt] = useState('');
  const [syncMessage, setSyncMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const today = todayMoscowDate();
        const [billingRes, layoutRes, todayRes, earningsRes, pendingRes] = await Promise.all([
          api.get('/payments/teacher/students/'),
          api.get('/teacher/students/layout/').catch(() => ({ data: [] })),
          api.get('/payments/teacher/lesson-reports/', { params: { date: today } }).catch(() => ({ data: [] })),
          api.get('/payments/teacher/earnings/').catch(() => ({ data: null })),
          api.get('/payments/teacher/pending-orders/').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setRows(Array.isArray(billingRes.data) ? billingRes.data : []);
        setLayoutItems(Array.isArray(layoutRes.data) ? layoutRes.data : []);
        setTodayReports(Array.isArray(todayRes.data) ? todayRes.data : []);
        const earnings = earningsRes?.data;
        setPeriodEarnings(
          earnings && typeof earnings === 'object' && 'this_week' in earnings ? earnings : null,
        );
        setPendingOrders(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Не удалось загрузить оплату учеников.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function syncPendingOrder(extId) {
    setSyncingExt(extId);
    setSyncMessage('');
    try {
      const res = await api.get(`/payments/orders/${extId}/`);
      const status = res.data?.status;
      if (status === 'paid') {
        setSyncMessage(`Зачислено ${formatMoney(res.data.amount)}.`);
      } else if (status === 'failed') {
        setSyncMessage('Озон сообщил, что оплата не прошла.');
      } else {
        setSyncMessage('Озон ещё не подтвердил оплату — заказ остаётся в списке.');
      }
      const pendingRes = await api.get('/payments/teacher/pending-orders/');
      setPendingOrders(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      if (status === 'paid') {
        const billingRes = await api.get('/payments/teacher/students/');
        setRows(Array.isArray(billingRes.data) ? billingRes.data : []);
      }
    } catch (e) {
      setSyncMessage(e.response?.data?.detail || 'Не удалось сверить заказ с Озоном.');
    } finally {
      setSyncingExt('');
    }
  }

  const totals = useMemo(() => uniqueAccountTotals(rows), [rows]);
  const displayRows = useMemo(() => mergeBillingRows(rows, layoutItems), [rows, layoutItems]);
  const today = useMemo(() => summarizeTodayReports(todayReports), [todayReports]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка…</p>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <div className="page-header">
        <h1>Оплата учеников</h1>
        <p className="page-subtitle">
          Предоплата и долг по семейным счетам. Нажмите на ученика, чтобы увидеть историю и оформить занятие.
        </p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {syncMessage && <div className="alert alert-warning">{syncMessage}</div>}

      {pendingOrders.length > 0 ? (
        <section className="billing-pending-orders" aria-label="Зависшие оплаты">
          <div className="billing-totals billing-pending-totals">
            <div className="billing-totals-main">
              <span className="billing-totals-label">Зависшие оплаты Озон</span>
              <strong>{pendingOrders.length}</strong>
            </div>
            <p className="billing-totals-note">
              Ссылка создана, а зачисление на сайт ещё не пришло. Показываем попытки
              за последние 2 суток. «Сверить» спрашивает Озон и кладёт деньги только
              если он уже подтвердил оплату.
            </p>
          </div>
          <table className="table billing-pending-table">
            <thead>
              <tr>
                <th>Ученик</th>
                <th>Сумма</th>
                <th>Создан</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pendingOrders.map((order) => (
                <tr key={order.ext_id}>
                  <td>
                    <Link to={`/teacher/billing/${order.student_id}`}>
                      {studentLabel(order)}
                      {order.username ? (
                        <span className="text-secondary"> @{order.username}</span>
                      ) : null}
                    </Link>
                  </td>
                  <td>{formatMoney(order.amount)}</td>
                  <td>{formatDateTime(order.created_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={syncingExt === order.ext_id}
                      onClick={() => syncPendingOrder(order.ext_id)}
                    >
                      {syncingExt === order.ext_id ? 'Сверяю…' : 'Сверить'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section className="billing-today" aria-label="Сегодня">
        <div className="billing-totals billing-today-totals">
          <div className="billing-totals-main">
            <span className="billing-totals-label">Сегодня выставлено</span>
            <strong>{formatMoney(today.total)}</strong>
          </div>
          <p className="billing-totals-note">
            списано {formatMoney(today.charged)}
            {' · '}
            ждёт ученика {formatMoney(today.pending)}
          </p>
          {periodEarnings ? (
            <p className="billing-totals-note billing-period-earnings">
              вчера {formatMoney(periodEarnings.yesterday)}
              {' · '}
              эта неделя {formatMoney(periodEarnings.this_week)}
              {' · '}
              прошлая неделя {formatMoney(periodEarnings.last_week)}
              {' · '}
              этот месяц {formatMoney(periodEarnings.this_month)}
              {' · '}
              прошлый месяц {formatMoney(periodEarnings.last_month)}
            </p>
          ) : null}
        </div>
        {today.reports.length === 0 ? (
          <p className="text-secondary billing-today-empty">Пока нет занятий за сегодня.</p>
        ) : (
          <table className="table billing-today-table">
            <thead>
              <tr>
                <th>Ученик</th>
                <th>Минуты</th>
                <th>Сумма</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {today.reports.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/teacher/billing/${r.student_id}`}>
                      {r.student_name || r.student_username || `Ученик #${r.student_id}`}
                    </Link>
                  </td>
                  <td>{r.duration_minutes}</td>
                  <td>{formatMoney(r.amount)}</td>
                  <td>{r.status_label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {rows.length === 0 ? (
        <p className="text-secondary">Нет активных учеников.</p>
      ) : (
        <>
          <div className="billing-totals">
            <div className="billing-totals-main">
              <span className="billing-totals-label">Общий баланс</span>
              <strong
                className={
                  totals.balance < 0
                    ? 'text-error'
                    : totals.balance > 0
                      ? 'text-success'
                      : undefined
                }
              >
                {formatMoney(totals.balance)}
              </strong>
            </div>
            <p className="billing-totals-note">
              предоплата {formatMoney(totals.prepaid)} − долг {formatMoney(totals.debt)}
            </p>
          </div>
          <table className="table billing-students-table">
            <thead>
              <tr>
                <th>Ученик</th>
                <th>Ставка</th>
                <th>Предоплата / долг</th>
                <th>Последний платёж</th>
                <th>Всего внесено</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((item) => {
                if (item.kind === 'divider') {
                  return (
                    <tr key={item.key} className="teacher-students-divider-row">
                      <td colSpan={5}>
                        <div className="teacher-students-divider">
                          <span className="teacher-students-divider-line" aria-hidden />
                          {item.title ? (
                            <span className="teacher-students-divider-title">{item.title}</span>
                          ) : null}
                          <span className="teacher-students-divider-line" aria-hidden />
                        </div>
                      </td>
                    </tr>
                  );
                }
                const row = item.row;
                return (
                  <tr key={item.key}>
                    <td>
                      <Link to={`/teacher/billing/${row.student_id}`}>
                        {studentLabel(row)}
                        {row.username ? (
                          <span className="text-secondary"> @{row.username}</span>
                        ) : null}
                      </Link>
                    </td>
                    <td>{row.rate_per_hour ? formatMoney(row.rate_per_hour) : '—'}</td>
                    <td className={row.status === 'debt' ? 'text-error' : 'text-success'}>
                      {row.status === 'debt'
                        ? `Долг ${formatMoney(row.debt)}`
                        : `Предоплата ${formatMoney(row.prepaid)}`}
                    </td>
                    <td>
                      {formatDateTime(row.last_payment_at)}
                      {row.last_payment_amount ? ` · ${formatMoney(row.last_payment_amount)}` : ''}
                    </td>
                    <td>{formatMoney(row.total_deposited)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
